import {FC} from "react"
import {useTranslation} from "react-i18next"
import {ArrowPathIcon, PlusIcon} from "@heroicons/react/20/solid"
import {twMerge} from "tailwind-merge"

import pipeDriveLogo from "../../../../assets/pipedrive_monogram_green_background.png"
import {UnstyledLink} from "../../../../components/Link"
import {ProfileImage} from "../../../../components/ProfileImage.tsx"
import {AAssignmentShort, AAssignmentStatuses, AMatchingSalesPerson} from "../../../../services/types.generated"
import {getFullName} from "../../../../utils"

export const isPending = (status: string | undefined): boolean => status === AAssignmentStatuses.Pending

const Package: FC<{
  assignment: AAssignmentShort
  highlighted: boolean
  selectedPerson?: AMatchingSalesPerson
  navigateRelative?: boolean
}> = ({assignment, highlighted, selectedPerson, navigateRelative}) => {
  const {t} = useTranslation()

  const matched = assignment.sales_person
  const pending = assignment.pending_assignment_offers?.[0]
  const person = matched ?? pending?.sales_person ?? selectedPerson

  const showPipeDriveLogo = person?.synced_with_crm

  return (
    <UnstyledLink
      to={`${navigateRelative ? "../" : ""}assignment/${assignment.id}`}
      className={twMerge([
        "group my-3 block w-80 shrink-0 rounded-full border p-2 outline outline-2 outline-white transition-all hover:outline-cr-blue-mid",
        highlighted && "pointer-events-none outline-cr-blue",
      ])}
    >
      <div className={"flex items-center justify-between"}>
        <div className={"flex items-center"}>
          <ProfileImage
            className={"inline-block size-9"}
            disabled={isPending(assignment.status)}
            src={person?.profile_picture_thumbnail_url ?? undefined}
          />
          <div className={"ml-3 leading-none"}>
            <div className={"relative inline-block"}>
              <span
                className={twMerge(
                  "inline-block max-w-48 overflow-hidden text-sm font-semibold text-ellipsis whitespace-nowrap text-cr-grey-50 group-hover:text-cr-grey-80",
                  highlighted && "text-cr-blue"
                )}
              >
                {person ? getFullName(person) : highlighted ? t("Matching_Matching") : t("Matching_Waiting for match")}
              </span>
              {showPipeDriveLogo && (
                <img
                  src={pipeDriveLogo}
                  alt={"Pipedrive logo"}
                  className={"absolute top-0 -right-4 h-auto w-3"}
                  title={t("Prospects_CRMSync_Synced")}
                />
              )}
            </div>
            <div
              className={
                "max-w-52 overflow-hidden text-xs font-medium text-ellipsis whitespace-nowrap text-cr-grey-50 group-hover:text-cr-grey-80"
              }
            >
              {t("Matching_{{reaches}} reaches", {reaches: assignment.reaches})}
            </div>
          </div>
        </div>
        <div className={"mx-2 text-cr-grey-50"}>
          {selectedPerson ? <ArrowPathIcon className={"h-6 w-6"} /> : !person && <PlusIcon className={"h-6 w-6"} />}
        </div>
      </div>
    </UnstyledLink>
  )
}

export default Package
